import React, { useState, useEffect } from 'react';

const HallazgosIDRPanel = ({ apiUrl, isMobile = false, currentUser = null }) => {
  const token = () => localStorage.getItem('auth_token') || localStorage.getItem('control_campo_token');
  const authHeaders = () => ({ Authorization: `Bearer ${token()}` });
  const isSuperAdmin = currentUser?.usuario_ffm === 'IGNOROSA' || currentUser?.role_name === 'SUPER_ADMIN';

  // Estados de carga de archivo
  const [file, setFile] = useState(null);
  const [semana, setSemana] = useState('');
  const [uploadLoading, setUploadLoading] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [uploadResult, setUploadResult] = useState(null);
  const [msg, setMsg] = useState(null); // { type: 'success' | 'error' | 'info', text: '' }

  // Estados de historial y registros
  const [activeSubTab, setActiveSubTab] = useState('upload'); // 'upload' | 'explorer' | 'history'
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  // Estados de explorador de registros
  const [records, setRecords] = useState([]);
  const [recordsLoading, setRecordsLoading] = useState(false);
  const [stats, setStats] = useState({ total_registrados: 0, verificados_si: 0, pendientes: 0, total_ots: 0, semanas_disponibles: [] });
  const [filters, setFilters] = useState({
    semana: '',
    estatus_verificacion: '',
    search: '',
    page: 1,
    page_size: 25
  });

  // Calcular semana actual por defecto
  useEffect(() => {
    const now = new Date();
    const d = new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate()));
    const dayNum = d.getUTCDay() || 7;
    d.setUTCDate(d.getUTCDate() + 4 - dayNum);
    const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
    const weekNo = Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
    setSemana(`Semana ${weekNo} - ${d.getUTCFullYear()}`);
  }, []);

  // Cargar historial
  const fetchHistory = async () => {
    setHistoryLoading(true);
    try {
      const res = await fetch(`${apiUrl}/admin/hallazgos-idr/history`, { headers: authHeaders() });
      if (res.ok) {
        const data = await res.json();
        setHistory(data);
      }
    } catch (e) {
      console.error("Error al obtener historial de cargas:", e);
    } finally {
      setHistoryLoading(false);
    }
  };

  // Cargar registros del explorador
  const fetchRecords = async () => {
    setRecordsLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.semana) params.append('semana', filters.semana);
      if (filters.estatus_verificacion) params.append('estatus_verificacion', filters.estatus_verificacion);
      if (filters.search) params.append('search', filters.search);
      params.append('page', filters.page);
      params.append('page_size', filters.page_size);

      const res = await fetch(`${apiUrl}/admin/hallazgos-idr/records?${params.toString()}`, { headers: authHeaders() });
      if (res.ok) {
        const data = await res.json();
        setRecords(data.records || []);
        if (data.stats) {
          setStats(data.stats);
        }
      }
    } catch (e) {
      console.error("Error al obtener registros:", e);
    } finally {
      setRecordsLoading(false);
    }
  };

  useEffect(() => {
    if (activeSubTab === 'history') {
      fetchHistory();
    } else if (activeSubTab === 'explorer') {
      fetchRecords();
    }
  }, [activeSubTab, filters.page, filters.semana, filters.estatus_verificacion]);

  const handleFileChange = (e) => {
    const selected = e.target.files[0];
    if (selected) {
      setFile(selected);
      setPreviewData(null);
      setUploadResult(null);
      setMsg(null);
    }
  };

  // Previsualizar archivo
  const handlePreview = async () => {
    if (!file) {
      setMsg({ type: 'error', text: 'Por favor selecciona un archivo (.xlsx o .csv)' });
      return;
    }
    setUploadLoading(true);
    setMsg(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('semana', semana);
      formData.append('preview', 'true');

      const res = await fetch(`${apiUrl}/admin/hallazgos-idr/upload`, {
        method: 'POST',
        headers: authHeaders(),
        body: formData
      });

      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.detail || 'Error procesando archivo');
      }

      setPreviewData(data);
      setMsg({ type: 'info', text: `Archivo leído correctamente: ${data.total_registros} filas encontradas. Revisa la muestra y confirma la carga.` });
    } catch (err) {
      setMsg({ type: 'error', text: err.message });
    } finally {
      setUploadLoading(false);
    }
  };

  // Confirmar y procesar carga a base de datos
  const handleConfirmUpload = async () => {
    if (!file) return;
    setUploadLoading(true);
    setMsg(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('semana', semana);
      formData.append('preview', 'false');

      const res = await fetch(`${apiUrl}/admin/hallazgos-idr/upload`, {
        method: 'POST',
        headers: authHeaders(),
        body: formData
      });

      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.detail || 'Error al guardar en base de datos');
      }

      setUploadResult(data);
      setPreviewData(null);
      setFile(null);
      // Reset input
      const fileInput = document.getElementById('hallazgos-file-input');
      if (fileInput) fileInput.value = '';

      setMsg({
        type: 'success',
        text: `✅ ${data.message}: Se procesaron ${data.total_procesados} registros (${data.registros_nuevos} nuevos, ${data.registros_actualizados} actualizados).`
      });
      fetchHistory();
    } catch (err) {
      setMsg({ type: 'error', text: err.message });
    } finally {
      setUploadLoading(false);
    }
  };

  // Descargar plantilla
  const handleDownloadTemplate = () => {
    window.open(`${apiUrl}/admin/hallazgos-idr/template`, '_blank');
  };

  // Eliminar lote de carga (Super Admin / IGNOROSA)
  const handleDeleteCarga = async (cargaId) => {
    if (!window.confirm(`¿Estás seguro de eliminar el lote #${cargaId} y todos sus registros de la base de datos?`)) return;
    try {
      const res = await fetch(`${apiUrl}/admin/hallazgos-idr/carga/${cargaId}`, {
        method: 'DELETE',
        headers: authHeaders()
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Error al eliminar');
      setMsg({ type: 'success', text: `✅ Lote #${cargaId} eliminado correctamente.` });
      fetchHistory();
      if (activeSubTab === 'explorer') fetchRecords();
    } catch (e) {
      setMsg({ type: 'error', text: e.message });
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
      
      {/* HEADER DE LA SECCIÓN */}
      <div style={{
        background: 'linear-gradient(135deg, #4C1D95 0%, #7C3AED 50%, #8B5CF6 100%)',
        padding: '24px',
        borderRadius: '16px',
        color: 'white',
        boxShadow: '0 10px 25px -5px rgba(124, 58, 237, 0.3)',
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'row',
        justifyContent: 'space-between',
        alignItems: isMobile ? 'flex-start' : 'center',
        gap: '16px'
      }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '6px' }}>
            <span style={{ fontSize: '28px' }}>📑</span>
            <h2 style={{ margin: 0, fontSize: '22px', fontWeight: 700 }}>Carga y Gestión de Hallazgos IDR</h2>
          </div>
          <p style={{ margin: 0, fontSize: '14px', opacity: 0.9 }}>
            Actualización semana a semana de hallazgos para Inspección de Red (IDR). Soporta archivos Excel (.xlsx) y CSV.
          </p>
        </div>
        <div style={{
          background: 'rgba(255, 255, 255, 0.15)',
          backdropFilter: 'blur(8px)',
          padding: '12px 18px',
          borderRadius: '12px',
          border: '1px solid rgba(255, 255, 255, 0.25)',
          fontSize: '13px',
          textAlign: isMobile ? 'left' : 'right'
        }}>
          <div style={{ fontWeight: 600, color: '#F3E8FF', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            {currentUser?.usuario_ffm === 'IGNOROSA' ? '👑 Modo Super Admin (Sin Restricciones)' : '👤 Usuario Autorizado'}
          </div>
          <div style={{ fontWeight: 700, fontSize: '15px', color: '#FFFFFF', marginTop: '2px' }}>
            {currentUser?.usuario_ffm === 'IGNOROSA' 
              ? 'IGNOROSA ADMIN (IGNOROSA)' 
              : `${currentUser?.usuario_ffm || 'TOT25CMXI2752'} (${currentUser?.full_name || 'Erika Morales'})`}
          </div>
          <div style={{ fontSize: '11px', color: '#DDD6FE', marginTop: '2px' }}>
            {currentUser?.usuario_ffm === 'IGNOROSA' 
              ? 'Gestión total de usuarios, reportes y cargas' 
              : `Rol: ${currentUser?.role_name || 'Supervisores IDR´s'}`}
          </div>
        </div>
      </div>

      {/* SUB-NAVEGACIÓN (TABS) */}
      <div style={{ display: 'flex', gap: '8px', borderBottom: '2px solid #E2E8F0', paddingBottom: '4px' }}>
        <button
          type="button"
          onClick={() => setActiveSubTab('upload')}
          style={{
            padding: '10px 20px',
            borderRadius: '10px',
            border: 'none',
            cursor: 'pointer',
            fontWeight: 600,
            fontSize: '14px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            background: activeSubTab === 'upload' ? '#8B5CF6' : 'transparent',
            color: activeSubTab === 'upload' ? 'white' : '#64748B',
            transition: 'all 0.2s'
          }}
        >
          <span>📤</span>
          <span>Cargar Archivo Semanal</span>
        </button>
        <button
          type="button"
          onClick={() => setActiveSubTab('explorer')}
          style={{
            padding: '10px 20px',
            borderRadius: '10px',
            border: 'none',
            cursor: 'pointer',
            fontWeight: 600,
            fontSize: '14px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            background: activeSubTab === 'explorer' ? '#8B5CF6' : 'transparent',
            color: activeSubTab === 'explorer' ? 'white' : '#64748B',
            transition: 'all 0.2s'
          }}
        >
          <span>🔍</span>
          <span>Consultar Hallazgos Guardados</span>
        </button>
        <button
          type="button"
          onClick={() => setActiveSubTab('history')}
          style={{
            padding: '10px 20px',
            borderRadius: '10px',
            border: 'none',
            cursor: 'pointer',
            fontWeight: 600,
            fontSize: '14px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            background: activeSubTab === 'history' ? '#8B5CF6' : 'transparent',
            color: activeSubTab === 'history' ? 'white' : '#64748B',
            transition: 'all 0.2s'
          }}
        >
          <span>📋</span>
          <span>Historial de Cargas</span>
        </button>
      </div>

      {/* MENSAJES DE ALERTA */}
      {msg && (
        <div style={{
          padding: '14px 18px',
          borderRadius: '10px',
          fontSize: '14px',
          fontWeight: 500,
          background: msg.type === 'error' ? '#FEE2E2' : msg.type === 'success' ? '#DCFCE7' : '#EFF6FF',
          color: msg.type === 'error' ? '#991B1B' : msg.type === 'success' ? '#166534' : '#1E40AF',
          border: `1px solid ${msg.type === 'error' ? '#F87171' : msg.type === 'success' ? '#86EFAC' : '#93C5FD'}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <div>{msg.text}</div>
          <button
            onClick={() => setMsg(null)}
            style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '16px', fontWeight: 'bold' }}
          >
            ×
          </button>
        </div>
      )}

      {/* VISTA 1: CARGA DE ARCHIVO SEMANAL */}
      {activeSubTab === 'upload' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
          
          <div style={{
            background: 'white',
            borderRadius: '16px',
            padding: '24px',
            border: '1px solid #E2E8F0',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <div>
                <h3 style={{ margin: '0 0 4px', fontSize: '18px', color: '#1E293B' }}>Subir Archivo de la Semana</h3>
                <p style={{ margin: 0, fontSize: '13px', color: '#64748B' }}>
                  Selecciona el archivo .xlsx o .csv que contiene los hallazgos levantados y verificados
                </p>
              </div>
              <button
                type="button"
                onClick={handleDownloadTemplate}
                style={{
                  background: '#F1F5F9',
                  color: '#475569',
                  border: '1px solid #CBD5E1',
                  borderRadius: '10px',
                  padding: '8px 16px',
                  fontSize: '13px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}
              >
                <span>📥</span>
                <span>Descargar Plantilla CSV</span>
              </button>
            </div>

            {/* FORMULARIO DE CARGA */}
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '2fr 1fr', gap: '20px', marginBottom: '20px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#475569', marginBottom: '8px' }}>
                  Archivo Excel (.xlsx / .xls) o CSV (.csv)
                </label>
                <input
                  id="hallazgos-file-input"
                  type="file"
                  accept=".xlsx, .xls, .csv"
                  onChange={handleFileChange}
                  style={{
                    width: '100%',
                    padding: '12px',
                    borderRadius: '10px',
                    border: '2px dashed #CBD5E1',
                    background: '#F8FAFC',
                    cursor: 'pointer',
                    fontSize: '14px'
                  }}
                />
                {file && (
                  <div style={{ marginTop: '8px', fontSize: '13px', color: '#6366F1', fontWeight: 600 }}>
                    📄 Archivo seleccionado: {file.name} ({(file.size / 1024).toFixed(1)} KB)
                  </div>
                )}
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#475569', marginBottom: '8px' }}>
                  Etiqueta de la Semana
                </label>
                <input
                  type="text"
                  value={semana}
                  onChange={(e) => setSemana(e.target.value)}
                  placeholder="Ej: Semana 42 - 2026"
                  style={{
                    width: '100%',
                    padding: '12px',
                    borderRadius: '10px',
                    border: '1px solid #CBD5E1',
                    fontSize: '14px',
                    boxSizing: 'border-box'
                  }}
                />
                <span style={{ fontSize: '12px', color: '#94A3B8', marginTop: '4px', display: 'block' }}>
                  Permite agrupar los datos por corte semanal
                </span>
              </div>
            </div>

            {/* BOTONES DE ACCIÓN */}
            <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
              <button
                type="button"
                onClick={handlePreview}
                disabled={!file || uploadLoading}
                style={{
                  background: '#6366F1',
                  color: 'white',
                  border: 'none',
                  borderRadius: '10px',
                  padding: '12px 24px',
                  fontSize: '14px',
                  fontWeight: 600,
                  cursor: !file || uploadLoading ? 'not-allowed' : 'pointer',
                  opacity: !file || uploadLoading ? 0.6 : 1,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
              >
                <span>👀</span>
                <span>{uploadLoading ? 'Leyendo archivo...' : 'Previsualizar Datos'}</span>
              </button>

              {previewData && (
                <button
                  type="button"
                  onClick={handleConfirmUpload}
                  disabled={uploadLoading}
                  style={{
                    background: '#10B981',
                    color: 'white',
                    border: 'none',
                    borderRadius: '10px',
                    padding: '12px 28px',
                    fontSize: '14px',
                    fontWeight: 700,
                    cursor: uploadLoading ? 'not-allowed' : 'pointer',
                    boxShadow: '0 4px 12px rgba(16, 185, 129, 0.3)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                >
                  <span>💾</span>
                  <span>{uploadLoading ? 'Guardando en BD...' : `Confirmar y Guardar (${previewData.total_registros} registros)`}</span>
                </button>
              )}
            </div>
          </div>

          {/* TABLA DE PREVISUALIZACIÓN */}
          {previewData && previewData.muestra && (
            <div style={{
              background: 'white',
              borderRadius: '16px',
              padding: '24px',
              border: '1px solid #E2E8F0',
              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                <div>
                  <h4 style={{ margin: 0, fontSize: '16px', color: '#1E293B' }}>
                    🔍 Muestra de Datos Detectados (Primeros 5 registros de {previewData.total_registros})
                  </h4>
                  <p style={{ margin: 0, fontSize: '12px', color: '#64748B' }}>
                    Semana asignada: <b>{previewData.semana}</b> | Archivo: <b>{previewData.filename}</b>
                  </p>
                </div>
              </div>

              <div style={{ overflowX: 'auto', border: '1px solid #E2E8F0', borderRadius: '12px' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                  <thead>
                    <tr style={{ background: '#F8FAFC', color: '#475569', textAlign: 'left', borderBottom: '2px solid #E2E8F0' }}>
                      <th style={{ padding: '10px 12px' }}>ID Hallazgo</th>
                      <th style={{ padding: '10px 12px' }}>OT</th>
                      <th style={{ padding: '10px 12px' }}>Inspector</th>
                      <th style={{ padding: '10px 12px' }}>Región / Distrito</th>
                      <th style={{ padding: '10px 12px' }}>Empresa</th>
                      <th style={{ padding: '10px 12px' }}>Subtipo</th>
                      <th style={{ padding: '10px 12px' }}>Usuario FFM</th>
                      <th style={{ padding: '10px 12px' }}>Estatus</th>
                      <th style={{ padding: '10px 12px' }}>Verificación</th>
                    </tr>
                  </thead>
                  <tbody>
                    {previewData.muestra.map((row, idx) => (
                      <tr key={idx} style={{ borderBottom: '1px solid #F1F5F9' }}>
                        <td style={{ padding: '10px 12px', fontWeight: 600, color: '#6366F1' }}>{row.id_hallazgo}</td>
                        <td style={{ padding: '10px 12px' }}>{row.ot}</td>
                        <td style={{ padding: '10px 12px' }}>{row.nombre_inspector}</td>
                        <td style={{ padding: '10px 12px' }}>{row.region_op} / {row.distrito_op}</td>
                        <td style={{ padding: '10px 12px' }}>{row.empresa_proveedor}</td>
                        <td style={{ padding: '10px 12px' }}>{row.subtipo}</td>
                        <td style={{ padding: '10px 12px' }}>{row.usuario_inspector}</td>
                        <td style={{ padding: '10px 12px' }}>
                          <span style={{
                            padding: '3px 8px', borderRadius: '12px', fontSize: '11px', fontWeight: 600,
                            background: row.estatus === 'Terminada' ? '#DCFCE7' : '#F1F5F9',
                            color: row.estatus === 'Terminada' ? '#166534' : '#475569'
                          }}>
                            {row.estatus || '-'}
                          </span>
                        </td>
                        <td style={{ padding: '10px 12px' }}>
                          <span style={{
                            padding: '3px 8px', borderRadius: '12px', fontSize: '11px', fontWeight: 600,
                            background: (row.estatus_verificacion || '').toLowerCase() === 'si' ? '#DCFCE7' : '#FEE2E2',
                            color: (row.estatus_verificacion || '').toLowerCase() === 'si' ? '#166534' : '#991B1B'
                          }}>
                            {row.estatus_verificacion || '-'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* GUÍA DE COLUMNAS OFICIALES */}
          <div style={{
            background: '#F8FAFC',
            borderRadius: '16px',
            padding: '20px',
            border: '1px solid #E2E8F0'
          }}>
            <h4 style={{ margin: '0 0 10px', fontSize: '14px', color: '#334155', fontWeight: 600 }}>
              📋 Formato de las 20 Columnas Oficiales Reconocidas:
            </h4>
            <div style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr' : 'repeat(4, 1fr)',
              gap: '8px',
              fontSize: '12px',
              color: '#64748B'
            }}>
              {[
                "1. Nombre (Inspector)", "2. ID HALLAZGO", "3. OT", "4. Longitud", "5. Latitud",
                "6. Region OP", "7. Distrito OP", "8. Empresa(proveedor)", "9. Usuario instalador", "10. Subtipo",
                "11. Cluster", "12. Area que Levantó", "13. Usuario (Inspector)", "14. Nombre (Instalador)", "15. Estatus",
                "16. Fecha creacion FFM", "17. Asignada", "18. Fecha termino", "19. Fecha Verificacion", "20. Estatus Verificacion"
              ].map((c, i) => (
                <div key={i} style={{ background: 'white', padding: '6px 10px', borderRadius: '6px', border: '1px solid #E2E8F0' }}>
                  {c}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* VISTA 2: EXPLORADOR Y CONSULTA DE HALLAZGOS EN BD */}
      {activeSubTab === 'explorer' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          
          {/* TARJETAS DE MÉTRICAS */}
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)', gap: '16px' }}>
            <div style={{ background: 'white', padding: '16px', borderRadius: '12px', border: '1px solid #E2E8F0' }}>
              <div style={{ fontSize: '12px', color: '#64748B', fontWeight: 600 }}>TOTAL REGISTRADOS</div>
              <div style={{ fontSize: '24px', fontWeight: 700, color: '#1E293B', marginTop: '4px' }}>
                {stats.total_registrados.toLocaleString()}
              </div>
            </div>
            <div style={{ background: 'white', padding: '16px', borderRadius: '12px', border: '1px solid #E2E8F0' }}>
              <div style={{ fontSize: '12px', color: '#166534', fontWeight: 600 }}>VERIFICADOS (SÍ)</div>
              <div style={{ fontSize: '24px', fontWeight: 700, color: '#10B981', marginTop: '4px' }}>
                {stats.verificados_si.toLocaleString()}
              </div>
            </div>
            <div style={{ background: 'white', padding: '16px', borderRadius: '12px', border: '1px solid #E2E8F0' }}>
              <div style={{ fontSize: '12px', color: '#991B1B', fontWeight: 600 }}>PENDIENTES / NO</div>
              <div style={{ fontSize: '24px', fontWeight: 700, color: '#EF4444', marginTop: '4px' }}>
                {stats.pendientes.toLocaleString()}
              </div>
            </div>
            <div style={{ background: 'white', padding: '16px', borderRadius: '12px', border: '1px solid #E2E8F0' }}>
              <div style={{ fontSize: '12px', color: '#6366F1', fontWeight: 600 }}>TOTAL DE OTS ÚNICAS</div>
              <div style={{ fontSize: '24px', fontWeight: 700, color: '#6366F1', marginTop: '4px' }}>
                {stats.total_ots.toLocaleString()}
              </div>
            </div>
          </div>

          {/* BARRA DE FILTROS */}
          <div style={{
            background: 'white',
            padding: '16px',
            borderRadius: '12px',
            border: '1px solid #E2E8F0',
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : 'repeat(4, 1fr)',
            gap: '12px',
            alignItems: 'end'
          }}>
            <div>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#475569', marginBottom: '4px' }}>
                Filtrar por Semana
              </label>
              <select
                value={filters.semana}
                onChange={(e) => setFilters({ ...filters, semana: e.target.value, page: 1 })}
                style={{ width: '100%', padding: '9px 12px', borderRadius: '8px', border: '1px solid #CBD5E1', fontSize: '13px' }}
              >
                <option value="">Todas las Semanas</option>
                {stats.semanas_disponibles.map((s, idx) => (
                  <option key={idx} value={s}>{s}</option>
                ))}
              </select>
            </div>

            <div>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#475569', marginBottom: '4px' }}>
                Estatus Verificación
              </label>
              <select
                value={filters.estatus_verificacion}
                onChange={(e) => setFilters({ ...filters, estatus_verificacion: e.target.value, page: 1 })}
                style={{ width: '100%', padding: '9px 12px', borderRadius: '8px', border: '1px solid #CBD5E1', fontSize: '13px' }}
              >
                <option value="">Todos los Estatus</option>
                <option value="Si">Verificado (Si)</option>
                <option value="-">Pendiente / No (-)</option>
              </select>
            </div>

            <div>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#475569', marginBottom: '4px' }}>
                Búsqueda Rápida
              </label>
              <input
                type="text"
                placeholder="ID Hallazgo, OT o Inspector..."
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                onKeyDown={(e) => { if (e.key === 'Enter') fetchRecords(); }}
                style={{ width: '100%', padding: '9px 12px', borderRadius: '8px', border: '1px solid #CBD5E1', fontSize: '13px', boxSizing: 'border-box' }}
              />
            </div>

            <div>
              <button
                type="button"
                onClick={fetchRecords}
                disabled={recordsLoading}
                style={{
                  width: '100%',
                  background: '#8B5CF6',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  padding: '10px',
                  fontSize: '13px',
                  fontWeight: 600,
                  cursor: 'pointer'
                }}
              >
                {recordsLoading ? 'Buscando...' : '🔍 Buscar'}
              </button>
            </div>
          </div>

          {/* TABLA DE RESULTADOS */}
          <div style={{ background: 'white', borderRadius: '16px', border: '1px solid #E2E8F0', overflow: 'hidden' }}>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                <thead>
                  <tr style={{ background: '#F8FAFC', color: '#475569', textAlign: 'left', borderBottom: '2px solid #E2E8F0' }}>
                    <th style={{ padding: '12px 14px' }}>ID Hallazgo</th>
                    <th style={{ padding: '12px 14px' }}>OT</th>
                    <th style={{ padding: '12px 14px' }}>Inspector</th>
                    <th style={{ padding: '12px 14px' }}>Región / Distrito</th>
                    <th style={{ padding: '12px 14px' }}>Empresa</th>
                    <th style={{ padding: '12px 14px' }}>Subtipo</th>
                    <th style={{ padding: '12px 14px' }}>Semana</th>
                    <th style={{ padding: '12px 14px' }}>Estatus FFM</th>
                    <th style={{ padding: '12px 14px' }}>Verificación</th>
                  </tr>
                </thead>
                <tbody>
                  {recordsLoading ? (
                    <tr>
                      <td colSpan="9" style={{ padding: '30px', textAlign: 'center', color: '#94A3B8' }}>
                        Cargando registros...
                      </td>
                    </tr>
                  ) : records.length === 0 ? (
                    <tr>
                      <td colSpan="9" style={{ padding: '30px', textAlign: 'center', color: '#94A3B8' }}>
                        No se encontraron hallazgos con los filtros seleccionados.
                      </td>
                    </tr>
                  ) : (
                    records.map((r) => (
                      <tr key={r.id} style={{ borderBottom: '1px solid #F1F5F9' }}>
                        <td style={{ padding: '12px 14px', fontWeight: 600, color: '#6366F1' }}>{r.id_hallazgo}</td>
                        <td style={{ padding: '12px 14px' }}>{r.ot}</td>
                        <td style={{ padding: '12px 14px' }}>
                          <div style={{ fontWeight: 600 }}>{r.nombre_inspector}</div>
                          <div style={{ fontSize: '11px', color: '#64748B' }}>{r.usuario_inspector}</div>
                        </td>
                        <td style={{ padding: '12px 14px' }}>{r.region_op} / {r.distrito_op}</td>
                        <td style={{ padding: '12px 14px' }}>{r.empresa_proveedor}</td>
                        <td style={{ padding: '12px 14px' }}>{r.subtipo}</td>
                        <td style={{ padding: '12px 14px' }}>
                          <span style={{ background: '#EDE9FE', color: '#6D28D9', padding: '3px 8px', borderRadius: '12px', fontSize: '11px', fontWeight: 600 }}>
                            {r.semana || '-'}
                          </span>
                        </td>
                        <td style={{ padding: '12px 14px' }}>
                          <span style={{
                            padding: '3px 8px', borderRadius: '12px', fontSize: '11px', fontWeight: 600,
                            background: r.estatus === 'Terminada' ? '#DCFCE7' : '#F1F5F9',
                            color: r.estatus === 'Terminada' ? '#166534' : '#475569'
                          }}>
                            {r.estatus || '-'}
                          </span>
                        </td>
                        <td style={{ padding: '12px 14px' }}>
                          <span style={{
                            padding: '3px 8px', borderRadius: '12px', fontSize: '11px', fontWeight: 600,
                            background: (r.estatus_verificacion || '').toLowerCase() === 'si' ? '#DCFCE7' : '#FEE2E2',
                            color: (r.estatus_verificacion || '').toLowerCase() === 'si' ? '#166534' : '#991B1B'
                          }}>
                            {r.estatus_verificacion || '-'}
                          </span>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* PAGINADOR */}
            <div style={{ padding: '14px 20px', background: '#F8FAFC', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: '13px', color: '#64748B' }}>
                Mostrando {records.length} de {stats.total_registrados} hallazgos
              </span>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button
                  type="button"
                  disabled={filters.page <= 1}
                  onClick={() => setFilters({ ...filters, page: filters.page - 1 })}
                  style={{
                    padding: '6px 14px', borderRadius: '8px', border: '1px solid #CBD5E1',
                    background: 'white', cursor: filters.page <= 1 ? 'not-allowed' : 'pointer',
                    fontSize: '13px', opacity: filters.page <= 1 ? 0.5 : 1
                  }}
                >
                  Anterior
                </button>
                <span style={{ padding: '6px 12px', fontSize: '13px', fontWeight: 600 }}>
                  Pág. {filters.page}
                </span>
                <button
                  type="button"
                  disabled={records.length < filters.page_size}
                  onClick={() => setFilters({ ...filters, page: filters.page + 1 })}
                  style={{
                    padding: '6px 14px', borderRadius: '8px', border: '1px solid #CBD5E1',
                    background: 'white', cursor: records.length < filters.page_size ? 'not-allowed' : 'pointer',
                    fontSize: '13px', opacity: records.length < filters.page_size ? 0.5 : 1
                  }}
                >
                  Siguiente
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* VISTA 3: HISTORIAL DE CARGAS */}
      {activeSubTab === 'history' && (
        <div style={{ background: 'white', borderRadius: '16px', border: '1px solid #E2E8F0', padding: '24px' }}>
          <h3 style={{ margin: '0 0 16px', fontSize: '18px', color: '#1E293B' }}>Historial de Lotes Semanales Subidos</h3>
          
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
              <thead>
                <tr style={{ background: '#F8FAFC', color: '#475569', textAlign: 'left', borderBottom: '2px solid #E2E8F0' }}>
                  <th style={{ padding: '12px 14px' }}>ID Lote</th>
                  <th style={{ padding: '12px 14px' }}>Fecha y Hora</th>
                  <th style={{ padding: '12px 14px' }}>Archivo</th>
                  <th style={{ padding: '12px 14px' }}>Semana</th>
                  <th style={{ padding: '12px 14px' }}>Total Filas</th>
                  <th style={{ padding: '12px 14px' }}>Nuevos</th>
                  <th style={{ padding: '12px 14px' }}>Actualizados</th>
                  <th style={{ padding: '12px 14px' }}>Cargado Por</th>
                  <th style={{ padding: '12px 14px' }}>Estado</th>
                  <th style={{ padding: '12px 14px', textAlign: 'center' }}>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {historyLoading ? (
                  <tr>
                    <td colSpan="10" style={{ padding: '30px', textAlign: 'center', color: '#94A3B8' }}>
                      Cargando historial...
                    </td>
                  </tr>
                ) : history.length === 0 ? (
                  <tr>
                    <td colSpan="10" style={{ padding: '30px', textAlign: 'center', color: '#94A3B8' }}>
                      Aún no hay lotes de hallazgos subidos al sistema.
                    </td>
                  </tr>
                ) : (
                  history.map((h) => (
                    <tr key={h.id} style={{ borderBottom: '1px solid #F1F5F9' }}>
                      <td style={{ padding: '12px 14px', fontWeight: 600 }}>#{h.id}</td>
                      <td style={{ padding: '12px 14px' }}>{h.fecha_carga}</td>
                      <td style={{ padding: '12px 14px', fontWeight: 600, color: '#334155' }}>{h.nombre_archivo}</td>
                      <td style={{ padding: '12px 14px' }}>
                        <span style={{ background: '#EDE9FE', color: '#6D28D9', padding: '3px 8px', borderRadius: '12px', fontSize: '11px', fontWeight: 600 }}>
                          {h.semana || '-'}
                        </span>
                      </td>
                      <td style={{ padding: '12px 14px', fontWeight: 600 }}>{h.total_registros.toLocaleString()}</td>
                      <td style={{ padding: '12px 14px', color: '#166534', fontWeight: 600 }}>+{h.registros_nuevos}</td>
                      <td style={{ padding: '12px 14px', color: '#2563EB', fontWeight: 600 }}>🔄 {h.registros_actualizados}</td>
                      <td style={{ padding: '12px 14px' }}>{h.cargado_por}</td>
                      <td style={{ padding: '12px 14px' }}>
                        <span style={{
                          padding: '4px 10px', borderRadius: '12px', fontSize: '11px', fontWeight: 600,
                          background: h.status === 'completado' ? '#DCFCE7' : '#FEE2E2',
                          color: h.status === 'completado' ? '#166534' : '#991B1B'
                        }}>
                          {h.status}
                        </span>
                      </td>
                      <td style={{ padding: '12px 14px', textAlign: 'center' }}>
                        {isSuperAdmin && (
                          <button
                            type="button"
                            onClick={() => handleDeleteCarga(h.id)}
                            style={{
                              background: '#FEE2E2',
                              color: '#DC2626',
                              border: '1px solid #FCA5A5',
                              borderRadius: '8px',
                              padding: '5px 10px',
                              fontSize: '11px',
                              fontWeight: 600,
                              cursor: 'pointer'
                            }}
                            title="Eliminar lote y registros"
                          >
                            🗑️ Eliminar
                          </button>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

    </div>
  );
};

export default HallazgosIDRPanel;
